import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { useRoutes } from '../hooks/useRoutes';
import './OnboardingModal.css';

const PROMPT_KEY = 'onboarding_prompt_seen_v1';
const LEGACY_KEY = 'seen_tutorial';
const COMPLETED_KEY = 'tutorial_completed_v1';

function wasOnboardingDismissed(): boolean {
  try {
    if (localStorage.getItem(PROMPT_KEY) || localStorage.getItem(COMPLETED_KEY)) return true;
    if (localStorage.getItem(LEGACY_KEY)) {
      localStorage.setItem(PROMPT_KEY, '1');
      return true;
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
  return false;
}

function StepVisual({ step }: { step: number }) {
  if (step === 0) {
    return (
      <svg viewBox="0 0 104 104" aria-hidden="true">
        <rect className="onboarding-modal__visual-fill" x="12" y="12" width="80" height="80" rx="16" />
        <g className="onboarding-modal__visual-stroke">
          <rect x="25" y="25" width="54" height="54" rx="5" />
          <path d="M43 25v54M61 25v54M25 43h54M25 61h54" />
        </g>
        <g className="onboarding-modal__visual-solid"><circle cx="34" cy="34" r="3"/><circle cx="52" cy="52" r="3"/><circle cx="70" cy="70" r="3"/></g>
      </svg>
    );
  }
  if (step === 1) {
    return (
      <svg viewBox="0 0 104 104" aria-hidden="true">
        <circle className="onboarding-modal__visual-fill" cx="52" cy="52" r="40" />
        <g className="onboarding-modal__visual-stroke">
          <path d="M31 69V43l21-12 21 12v26M39 48h26M39 57h26M39 66h26" />
          <circle className="onboarding-modal__visual-surface" cx="38" cy="32" r="7" />
          <path d="M35 32h6M38 29v6" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 104 104" aria-hidden="true">
      <circle className="onboarding-modal__visual-fill" cx="52" cy="52" r="40" />
      <circle className="onboarding-modal__visual-surface onboarding-modal__visual-stroke" cx="52" cy="52" r="27" />
      <path className="onboarding-modal__visual-check" d="M38 53l9 9 20-22" />
    </svg>
  );
}

export function OnboardingModal() {
  const { t } = useI18n();
  const { nav } = useRoutes();
  const { user, loading } = useAuth();
  const [dismissed, setDismissed] = useState(wasOnboardingDismissed);
  const [step, setStep] = useState(0);
  const [referenceTime] = useState(() => Date.now());
  const dialogRef = useRef<HTMLElement>(null);
  const onboarding = t.onboarding;

  const accountAge = user?.created_at
    ? referenceTime - new Date(user.created_at).getTime()
    : 0;
  const visible = !loading
    && !dismissed
    && !user?.has_played_before
    && accountAge <= 7 * 24 * 60 * 60 * 1000;

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(PROMPT_KEY, '1');
    } catch {
      // The in-memory state still prevents the modal reopening this session.
    }
    setDismissed(true);
  }, []);

  const startTutorial = useCallback(() => {
    try {
      localStorage.setItem(PROMPT_KEY, '1');
    } catch {
      // Navigation should continue even when storage is unavailable.
    }
    setDismissed(true);
    nav('tutorial');
  }, [nav]);

  useEffect(() => {
    if (!visible) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFrame = requestAnimationFrame(() => {
      const firstControl = dialog?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      (firstControl ?? dialog)?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
      if (event.key === 'ArrowLeft' && step > 0) setStep(current => current - 1);
      if (event.key === 'ArrowRight' && step < 2) setStep(current => current + 1);
      if (event.key === 'Tab') {
        const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []);
        if (controls.length === 0) {
          event.preventDefault();
          dialogRef.current?.focus();
          return;
        }
        const first = controls[0];
        const last = controls[controls.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismiss, visible, step]);

  if (!visible) return null;

  const titles = [onboarding.welcomeTitle, onboarding.toolsTitle, onboarding.readyTitle];
  const descriptions = [onboarding.welcomeDesc, onboarding.toolsDesc, onboarding.readyDesc];

  return (
    <div className="onboarding-modal" onMouseDown={dismiss}>
      <section
        ref={dialogRef}
        className="onboarding-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-modal-title"
        aria-describedby="onboarding-modal-description"
        tabIndex={-1}
        onMouseDown={event => event.stopPropagation()}
      >
        <button type="button" className="onboarding-modal__close" onClick={dismiss} aria-label={t.common.close}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>

        <div className="onboarding-modal__visual"><StepVisual step={step} /></div>
        <h2 id="onboarding-modal-title">{titles[step]}</h2>
        <p id="onboarding-modal-description">{descriptions[step]}</p>

        <div className="onboarding-modal__progress" aria-hidden="true">
          {[0, 1, 2].map(index => <span key={index} className={index === step ? 'is-active' : ''} />)}
        </div>

        {step === 2 ? (
          <div className="onboarding-modal__final-actions">
            <button type="button" className="onboarding-modal__primary" onClick={startTutorial}>{onboarding.startTutorial}</button>
            <button type="button" className="onboarding-modal__secondary" onClick={dismiss}>{onboarding.skipTutorial}</button>
          </div>
        ) : (
          <div className="onboarding-modal__actions">
            <button type="button" className="onboarding-modal__skip" onClick={dismiss}>{onboarding.skipTutorial}</button>
            <div className="onboarding-modal__navigation">
              {step > 0 && (
                <button type="button" className="onboarding-modal__icon-button" onClick={() => setStep(current => current - 1)} aria-label={t.tutorial.prev}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
              )}
              <button type="button" className="onboarding-modal__next" onClick={() => setStep(current => current + 1)}>
                {t.tutorial.next}
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
